/**
 * The consolation scorecard: a PNG a player who ran out of time can download and
 * send to the DLSM Team. Sending it is the effort that earns the credit, which is
 * also what keeps it from being farmed — a bot will not send a WhatsApp message.
 *
 * Drawn on a canvas so it ships with the page and needs no server round trip.
 */

const WIDTH = 1080;
const HEIGHT = 1350;
const BLUE = '#3362ff';
const INK = '#000a1e';
const PANEL = '#0f1a30';
const PALE = '#d2e4ff';
const MUTED = '#9ea5b4';
const FONT = "'HarmonyOS Sans SC','PingFang SC',Inter,system-ui,-apple-system,sans-serif";

export interface ScorecardInput {
  makes: number;
  target: number;
  /** Short code the DLSM Team can quote back. Ties the card to one run. */
  code: string;
  logoUrl?: string;
}

export async function buildScorecard(input: ScorecardInput): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not draw the scorecard.');

  // Fonts have to be resolved before any text is drawn or the card bakes the fallback.
  try {
    await document.fonts.ready;
  } catch {
    /* older browser: draw with whatever is resolved */
  }

  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // Court glow behind the number.
  const glow = ctx.createRadialGradient(WIDTH / 2, 560, 40, WIDTH / 2, 560, 620);
  glow.addColorStop(0, 'rgba(51, 98, 255, 0.42)');
  glow.addColorStop(1, 'rgba(51, 98, 255, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  ctx.textAlign = 'center';

  ctx.fillStyle = BLUE;
  ctx.font = `800 34px ${FONT}`;
  ctx.letterSpacing = '6px';
  ctx.fillText('DLSM HOOPS', WIDTH / 2, 178);
  ctx.letterSpacing = '0px';

  // "SO CLOSE" over a score of nothing reads as a lie. The headline follows the score.
  const headline =
    input.makes >= input.target - 1 ? 'SO CLOSE' : input.makes >= 2 ? 'GOOD RUN' : 'FIRST RUN';
  ctx.fillStyle = '#ffffff';
  ctx.font = `900 96px ${FONT}`;
  ctx.fillText(headline, WIDTH / 2, 300);

  ctx.fillStyle = PALE;
  ctx.font = `400 40px ${FONT}`;
  ctx.fillText(input.makes === 0 ? 'Clock beat you on' : 'Ran out of clock on', WIDTH / 2, 400);

  // The score itself.
  ctx.fillStyle = '#ffffff';
  ctx.font = `900 340px ${FONT}`;
  ctx.fillText(String(input.makes), WIDTH / 2, 690);
  ctx.fillStyle = MUTED;
  ctx.font = `600 48px ${FONT}`;
  ctx.fillText(`of ${input.target} baskets`, WIDTH / 2, 760);

  // The offer block. Every measurement below is taken, not assumed, so re-wording
  // the paragraph can never push the reference code on top of it again.
  const blockX = 90;
  const blockTop = 826;
  const blockWidth = WIDTH - 180;
  const textWidth = blockWidth - 100;
  const headingSize = 46;
  const bodySize = 34;
  const bodyLeading = 46;
  const codeSize = 40;
  const padding = 44;

  const body = 'Send this card to the DLSM Team with the reference below. Trade credit is applied by a person, not by the game.';
  ctx.font = `400 ${bodySize}px ${FONT}`;
  const lines = wrapLines(ctx, body, textWidth);

  const blockHeight =
    padding + headingSize + 34 + lines.length * bodyLeading + 30 + codeSize + padding;

  roundedRect(ctx, blockX, blockTop, blockWidth, blockHeight, 28);
  ctx.fillStyle = PANEL;
  ctx.fill();
  ctx.strokeStyle = 'rgba(210, 228, 255, 0.18)';
  ctx.lineWidth = 2;
  ctx.stroke();

  let y = blockTop + padding + headingSize;
  ctx.fillStyle = '#ffffff';
  ctx.font = `800 ${headingSize}px ${FONT}`;
  ctx.fillText('Send this card, still get credit', WIDTH / 2, y);

  y += 34;
  ctx.fillStyle = PALE;
  ctx.font = `400 ${bodySize}px ${FONT}`;
  for (const line of lines) {
    y += bodyLeading;
    ctx.fillText(line, WIDTH / 2, y);
  }

  y += 30 + codeSize;
  ctx.fillStyle = BLUE;
  ctx.font = `700 ${codeSize}px ${FONT}`;
  ctx.letterSpacing = '4px';
  ctx.fillText(input.code.toUpperCase(), WIDTH / 2, y);
  ctx.letterSpacing = '0px';

  const footerTop = blockTop + blockHeight;
  ctx.fillStyle = MUTED;
  ctx.font = `400 28px ${FONT}`;
  ctx.fillText(new Date().toISOString().slice(0, 10), WIDTH / 2, footerTop + 70);
  ctx.fillStyle = BLUE;
  ctx.font = `700 32px ${FONT}`;
  ctx.fillText('dlsm.com', WIDTH / 2, footerTop + 122);

  if (input.logoUrl) {
    try {
      const logo = await loadImage(input.logoUrl);
      // Sits clear of the wordmark below it rather than clipping its caps.
      ctx.drawImage(logo, WIDTH / 2 - 44, 34, 88, 88);
    } catch {
      /* the card is complete without it */
    }
  }

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the scorecard.'))), 'image/png');
  });
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Split text to fit `maxWidth` at the context's CURRENT font. Measure, then draw. */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('image failed'));
    image.src = url;
  });
}
